import React, { useState, useEffect } from 'react';
import type { UserFilters, UserRole, UserStatus } from '@lnr-v2/types';
import { Icon } from '@lnr-v2/ui';
import styles from './UserTableFilters.module.css';

export interface UserTableFiltersProps {
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
  resultCount?: number;
}

export const UserTableFilters: React.FC<UserTableFiltersProps> = ({
  filters,
  onFiltersChange,
  resultCount,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || '');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: localSearch });
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  const handleRoleChange = (role: UserRole | 'all') => {
    onFiltersChange({ ...filters, role });
  };

  const handleStatusChange = (status: UserStatus | 'all') => {
    onFiltersChange({ ...filters, status });
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    onFiltersChange({ search: '', role: 'all', status: 'all' });
  };

  const hasActiveFilters =
    filters.search ||
    (filters.role && filters.role !== 'all') ||
    (filters.status && filters.status !== 'all');

  return (
    <div className={styles.container}>
      {/* Search input */}
      <div className={styles.searchWrapper}>
        <Icon name="search" size="sm" className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className={styles.searchInput}
        />
        {localSearch && (
          <button
            onClick={() => setLocalSearch('')}
            className={styles.clearButton}
            title="Clear search"
          >
            <Icon name="x" size="sm" />
          </button>
        )}
      </div>

      {/* Role filter */}
      <div className={styles.filterGroup}>
        <label className={styles.filterLabel}>Role</label>
        <select
          value={filters.role || 'all'}
          onChange={(e) => handleRoleChange(e.target.value as UserRole | 'all')}
          className={styles.select}
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>

      {/* Status filter */}
      <div className={styles.filterGroup}>
        <label className={styles.filterLabel}>Status</label>
        <select
          value={filters.status || 'all'}
          onChange={(e) =>
            handleStatusChange(e.target.value as UserStatus | 'all')
          }
          className={styles.select}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <button
          onClick={handleClearFilters}
          className={styles.clearFiltersButton}
        >
          <Icon name="x" size="sm" />
          Clear filters
        </button>
      )}

      {/* Result count */}
      {resultCount !== undefined && (
        <div className={styles.resultCount}>
          {resultCount} {resultCount === 1 ? 'user' : 'users'} found
        </div>
      )}
    </div>
  );
};
